import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ConfigService } from './config.service';

export interface QuestionSummaryItem {
  yearmonth: string;
  count: number;
}

export interface QuestionSummaryResponse {
  yearMonths: QuestionSummaryItem[];
}

export interface QuestionUploadResponse {
  imported: number;
  errors: string[];
}

export interface DefaultModelResponse {
  model: string;
  allowed: string[];
}

export interface AllowCopyPasteResponse {
  allowCopyPaste: boolean;
}

/** User record from admin users list (camelCase from DynamoDB) */
export interface AdminUser {
  userId: string;
  email: string;
  role?: string;
  displayName?: string | null;
  profileImageUrl?: string | null;
  preferredLanguage?: string;
  subscriptionTier: string;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  examDate?: string | null;
  goal?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  count: number;
}

export interface AdminUserSubscriptionUpdate {
  subscriptionTier?: string;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/admin`;
  private readonly configService = inject(ConfigService);

  private cachedSummary: QuestionSummaryItem[] | null = null;
  private defaultModelLoaded = false;

  readonly uploadLoading = signal(false);
  readonly uploadError = signal<string | null>(null);
  readonly summaryLoading = signal(false);
  readonly summaryError = signal<string | null>(null);
  readonly modelLoading = signal(false);
  readonly modelSaving = signal(false);
  readonly modelError = signal<string | null>(null);
  readonly defaultModel = signal<string>('gpt-4o-mini');
  readonly allowedModels = signal<string[]>(['gpt-4o-mini', 'gpt-4.1-mini', 'gpt-5-mini', 'gpt-5.1']);
  readonly allowCopyPaste = signal<boolean>(false);
  readonly allowCopyPasteLoading = signal(false);
  readonly allowCopyPasteSaving = signal(false);
  readonly allowCopyPasteError = signal<string | null>(null);

  uploadExcel(file: File): Promise<QuestionUploadResponse> {
    this.uploadLoading.set(true);
    this.uploadError.set(null);

    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      this.http
        .post<QuestionUploadResponse>(`${this.apiUrl}/questions/upload`, formData)
        .subscribe({
          next: (res) => {
            this.uploadLoading.set(false);
            resolve(res);
          },
          error: (err) => {
            this.uploadLoading.set(false);
            const msg = err?.error?.detail ?? err?.message ?? 'Upload failed';
            this.uploadError.set(typeof msg === 'string' ? msg : JSON.stringify(msg));
            reject(err);
          },
        });
    });
  }

  fetchSummary(forceRefresh = false): Promise<QuestionSummaryResponse> {
    if (this.cachedSummary !== null && !forceRefresh) {
      return Promise.resolve({ yearMonths: this.cachedSummary });
    }

    this.summaryLoading.set(true);
    this.summaryError.set(null);

    return new Promise((resolve, reject) => {
      this.http.get<QuestionSummaryResponse>(`${this.apiUrl}/questions/summary`).subscribe({
        next: (res) => {
          this.cachedSummary = res.yearMonths;
          this.summaryLoading.set(false);
          resolve(res);
        },
        error: (err) => {
          this.cachedSummary = null;
          this.summaryLoading.set(false);
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to load summary';
          this.summaryError.set(typeof msg === 'string' ? msg : JSON.stringify(msg));
          reject(err);
        },
      });
    });
  }

  fetchDefaultModel(): Promise<DefaultModelResponse> {
    if (this.defaultModelLoaded) {
      return Promise.resolve({
        model: this.defaultModel(),
        allowed: this.allowedModels(),
      });
    }

    this.modelLoading.set(true);
    this.modelError.set(null);

    return new Promise((resolve, reject) => {
      this.http.get<DefaultModelResponse>(`${this.apiUrl}/config/default-model`).subscribe({
        next: (res) => {
          this.defaultModelLoaded = true;
          this.modelLoading.set(false);
          this.defaultModel.set(res.model);
          this.allowedModels.set(res.allowed ?? []);
          resolve(res);
        },
        error: (err) => {
          this.defaultModelLoaded = false;
          this.modelLoading.set(false);
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to load default model';
          this.modelError.set(typeof msg === 'string' ? msg : JSON.stringify(msg));
          reject(err);
        },
      });
    });
  }

  fetchAllowCopyPaste(): Promise<AllowCopyPasteResponse> {
    this.allowCopyPasteLoading.set(true);
    this.allowCopyPasteError.set(null);

    return new Promise((resolve, reject) => {
      this.http.get<AllowCopyPasteResponse>(`${this.apiUrl}/config/allow-copy-paste`).subscribe({
        next: (res) => {
          this.allowCopyPaste.set(res?.allowCopyPaste ?? false);
          this.allowCopyPasteLoading.set(false);
          resolve(res);
        },
        error: (err) => {
          this.allowCopyPasteLoading.set(false);
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to load allow copy/paste';
          this.allowCopyPasteError.set(typeof msg === 'string' ? msg : JSON.stringify(msg));
          reject(err);
        },
      });
    });
  }

  setAllowCopyPaste(allow: boolean): Promise<AllowCopyPasteResponse> {
    this.allowCopyPasteSaving.set(true);
    this.allowCopyPasteError.set(null);

    return new Promise((resolve, reject) => {
      this.http.put<AllowCopyPasteResponse>(`${this.apiUrl}/config/allow-copy-paste`, { allowCopyPaste: allow }).subscribe({
        next: (res) => {
          this.allowCopyPasteSaving.set(false);
          const newValue = res?.allowCopyPaste ?? allow;
          this.allowCopyPaste.set(newValue);
          this.configService.allowCopyPaste.set(newValue);
          resolve(res);
        },
        error: (err) => {
          this.allowCopyPasteSaving.set(false);
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to save allow copy/paste';
          this.allowCopyPasteError.set(typeof msg === 'string' ? msg : JSON.stringify(msg));
          reject(err);
        },
      });
    });
  }

  setDefaultModel(model: string): Promise<{ model: string }> {
    this.modelSaving.set(true);
    this.modelError.set(null);

    return new Promise((resolve, reject) => {
      this.http.put<{ model: string }>(`${this.apiUrl}/config/default-model`, { model }).subscribe({
        next: (res) => {
          this.modelSaving.set(false);
          this.defaultModel.set(res.model);
          resolve(res);
        },
        error: (err) => {
          this.modelSaving.set(false);
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to save model';
          this.modelError.set(typeof msg === 'string' ? msg : JSON.stringify(msg));
          reject(err);
        },
      });
    });
  }

  fetchAllUsers(): Promise<AdminUsersResponse> {
    return new Promise((resolve, reject) => {
      this.http.get<AdminUsersResponse>(`${this.apiUrl}/users`).subscribe({
        next: (res) => resolve(res),
        error: (err) => {
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to load users';
          reject(err);
        },
      });
    });
  }

  updateUserSubscription(
    userId: string,
    payload: AdminUserSubscriptionUpdate
  ): Promise<AdminUser> {
    return new Promise((resolve, reject) => {
      this.http.put<AdminUser>(`${this.apiUrl}/users/${userId}`, payload).subscribe({
        next: (res) => resolve(res),
        error: (err) => {
          const msg = err?.error?.detail ?? err?.message ?? 'Failed to update user';
          reject(err);
        },
      });
    });
  }
}
